import type { InputProps } from '../input';

import { forwardRef, useEffect, useRef, useState } from 'react';
import { isNil } from 'lodash';

import { Input } from '../input';

export const currencyWithSignFormatter = Intl.NumberFormat('en-AU', {
  style: 'currency',
  currency: 'AUD',
  maximumFractionDigits: 2,
});

export const currencyFormatter = Intl.NumberFormat('en-AU', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

/**
 * Formats a number as a currency string.
 *
 * @param value - The value to format
 * @param options - Formatting options
 * @param options.withSign - Whether to include the currency sign
 * @param options.gstRate - GST Rate included
 * @returns Formatted currency string
 */
export function formatCurrency(
  value: string | number = '',
  options?: {
    withSign?: boolean;
    gstRate?: number;
  },
) {
  const { withSign = false, gstRate = 0 } = options ?? {};

  if (value === '') {
    return '';
  }

  let valueAsNumber =
    typeof value === 'string' ? Number.parseFloat(value.replaceAll(',', '')) : value;

  if (Number.isNaN(valueAsNumber)) {
    return '';
  }

  if (gstRate) {
    valueAsNumber = Math.round(valueAsNumber * (100 + gstRate)) / 100;
  }

  return withSign
    ? currencyWithSignFormatter.format(valueAsNumber)
    : currencyFormatter.format(valueAsNumber);
}

export interface CurrencyInputProps
  extends Omit<
    InputProps,
    'type' | 'inputMode' | 'step' | 'autoComplete' | 'autoCapitalize' | 'autoCorrect'
  > {
  value?: string | number;
  defaultValue?: string | number;
  largeFontSize?: boolean;
  /**
   * Report each keystroke to `onChange`, rather than only the formatted value on blur.
   *
   * Off by default, and deliberately so: currency fields all over the app feed forms that
   * save, price or recalculate whenever a value changes, and committing per character would
   * put all of that on the keystroke. Turn it on where the value gates something the reader
   * can act on while the field still has focus — an amount over its ceiling otherwise sits
   * there with no error beside it and a live button beneath it.
   */
  commitOnChange?: boolean;
}

export const CurrencyInput = forwardRef<HTMLInputElement, CurrencyInputProps>(
  (
    {
      value,
      defaultValue,
      min = 0,
      max,
      placeholder = '0.00',
      prefix = '$',
      largeFontSize,
      commitOnChange,
      onBlur,
      onChange,
      onKeyDown,
      className,
      ...restProps
    },
    ref,
  ) => {
    const mountedRef = useRef(false);
    /** What this input last sent out, so the sync below can tell it apart from a new value. */
    const committedRef = useRef<string>();
    const [displayValue, setDisplayValue] = useState(() => formatCurrency(value ?? defaultValue));

    const commit = (event: React.ChangeEvent<HTMLInputElement>) => {
      committedRef.current = event.currentTarget.value;
      onChange?.(event);
    };

    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
      if (Number(min) < 0 && event.currentTarget.value === '-') {
        setDisplayValue(event.currentTarget.value);
        return;
      }
      const valueAsNumber = Number.parseFloat(event.currentTarget.value.replaceAll(',', ''));

      if (Number.isNaN(valueAsNumber)) {
        event.currentTarget.value = '';
        setDisplayValue('');
        if (commitOnChange) {
          commit(event);
        }
        return;
      }

      setDisplayValue(event.currentTarget.value);

      if (commitOnChange) {
        commit(event);
      }
    };

    const formatInputValue = (inputValue: string) => {
      let newValue = '';
      const valueAsNumber = Number.parseFloat(inputValue);

      if (Number.isNaN(valueAsNumber)) {
        newValue = '';
      } else if (
        (Number(min) >= 0 && valueAsNumber > 0 && valueAsNumber < 0.005) ||
        (Number(min) < 0 && valueAsNumber < 0 && valueAsNumber > -0.005)
      ) {
        newValue = '0.00';
      } else if (min && valueAsNumber < Number(min)) {
        newValue = currencyFormatter.format(Number(min));
      } else if (!isNil(max) && valueAsNumber > Number(max)) {
        newValue = currencyFormatter.format(Number(max));
      } else {
        newValue = currencyFormatter.format(valueAsNumber);
      }

      return newValue;
    };

    const handleBlur = (event: React.FocusEvent<HTMLInputElement>) => {
      const value = event.currentTarget.value.replaceAll(',', '');
      const newValue = formatInputValue(value);
      event.currentTarget.value = newValue.replaceAll(',', '');
      commit(event as unknown as React.ChangeEvent<HTMLInputElement>);
      onBlur?.(event);
      setDisplayValue(newValue);
    };

    const handleKeyDown: React.KeyboardEventHandler<HTMLInputElement> = (event) => {
      if (['Tab', 'Enter'].includes(event.key)) {
        event.currentTarget.blur();
      }
      onKeyDown?.(event);
    };

    useEffect(() => {
      if (!mountedRef.current) {
        mountedRef.current = true;
        return;
      }

      // A value coming back from what this input just sent is not news, and reformatting it
      // mid-word would rewrite what the reader is still typing ("5" landing back as "5.00").
      // Anything else — a quick-fill button, a reset, a new claim — is theirs to show.
      if (committedRef.current !== undefined && String(value ?? '') === committedRef.current) {
        return;
      }

      committedRef.current = undefined;
      setDisplayValue(formatCurrency(value));
    }, [value]);

    return (
      <Input
        ref={ref}
        value={displayValue}
        onBlur={handleBlur}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        className={className}
        inputClassName="text-right"
        {...restProps}
        inputMode="decimal"
        min={min}
        step={1}
        max={max}
        placeholder={placeholder}
        autoComplete="off"
        autoCapitalize="none"
        autoCorrect="off"
        prefix={prefix}
        transparentPrefix
        largeFontSize={largeFontSize}
      />
    );
  },
);

CurrencyInput.displayName = 'CurrencyInput';
